import Image from "next/image";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { websiteBodyText, websiteEyebrowText, websiteSectionTitleText } from "./website-type-scale";

const connectors = [
  {
    name: "WooCommerce",
    logo: "/website/connectors/woocommerce.svg",
    tagline: "Orders, products, and revenue — live.",
    kind: "source" as const,
  },
  {
    name: "Google Ads",
    logo: "/website/connectors/google-ads.svg",
    tagline: "Spend and conversions, always in context.",
    kind: "source" as const,
  },
  {
    name: "Meta Ads",
    logo: "/website/connectors/meta.svg",
    tagline: "Campaign reach and performance, tracked.",
    kind: "source" as const,
  },
  {
    name: "Google Analytics 4",
    logo: "/website/connectors/google-analytics.svg",
    tagline: "Traffic and behavior signals, unified.",
    kind: "source" as const,
  },
  {
    name: "Google Search Console",
    logo: "/website/connectors/google-search-console.svg",
    tagline: "Organic visibility and queries, surfaced.",
    kind: "source" as const,
  },
  {
    name: "Google Merchant Center",
    logo: "/website/connectors/google-merchant-center.svg",
    tagline: "Product feeds and shopping performance.",
    kind: "source" as const,
  },
  {
    name: "WhatsApp",
    logo: "/website/connectors/whatsapp.svg",
    tagline: "Ask from WhatsApp — your whole stack answers.",
    kind: "channel" as const,
  },
];

export function IntegrationsSection() {
  return (
    <section className="py-24 bg-slate-950 border-t border-slate-900/50">
      <div className="container mx-auto px-4 md:px-6">
        <ScrollReveal>
          <div className="text-center mb-16">
            <p className={`mb-4 text-purple-300 ${websiteEyebrowText}`}>Integrations</p>
            <h2 className={`mx-auto max-w-3xl text-white ${websiteSectionTitleText}`}>
              One chat across{" "}
              <span className="bg-gradient-to-r from-purple-200 via-white to-cyan-200 bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(192,132,252,0.4)]">
                your entire stack
              </span>
              .
            </h2>
            <p className={`mx-auto mt-5 max-w-xl text-slate-400 ${websiteBodyText}`}>
              Your connectors feed one conversation — not seven dashboards.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={150}>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {connectors.map((connector) => (
              <div
                key={connector.name}
                className={`group flex flex-col items-center gap-4 rounded-2xl border p-6 text-center transition-colors duration-200 ${
                  connector.kind === "channel"
                    ? "border-emerald-400/20 bg-emerald-400/5 hover:border-emerald-400/35 hover:bg-emerald-400/8"
                    : "border-slate-800 bg-slate-900/50 hover:border-slate-700 hover:bg-slate-900/80"
                }`}
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center">
                  <Image
                    src={connector.logo}
                    alt={connector.name}
                    width={48}
                    height={48}
                    className="h-10 w-10 object-contain"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-white">{connector.name}</span>
                  <span className="text-xs leading-relaxed text-slate-400">{connector.tagline}</span>
                </div>
                {connector.kind === "channel" && (
                  <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-300">
                    Access channel
                  </span>
                )}
              </div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
